import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, ImagePlus } from "lucide-react";

type TaskImageUploadProps = {
  taskId: string;
  onUploadComplete?: () => void;
};

export const TaskImageUpload = ({ taskId, onUploadComplete }: TaskImageUploadProps) => {
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setUploading(true);
      const file = event.target.files?.[0];
      
      if (!file) {
        toast({
          title: "Ingen fil valgt",
          description: "Vælg venligst en billedfil",
          variant: "destructive",
        });
        return;
      }

      // Validér filtype
      if (!file.type.startsWith('image/')) {
        toast({
          title: "Ugyldig filtype",
          description: "Du kan kun uploade billedfiler",
          variant: "destructive",
        });
        return;
      }

      // Generer et unikt filnavn
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `${taskId}/${fileName}`;

      // Upload fil til storage
      const { error: uploadError } = await supabase.storage
        .from('task-images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Gem metadata i databasen med automatisk realtidsopdatering
      const { error: dbError } = await supabase
        .from('task_images')
        .insert({
          task_id: taskId,
          file_path: filePath,
          file_name: file.name,
          content_type: file.type,
        });

      if (dbError) throw dbError;

      toast({
        title: "Billede uploadet",
        description: "Billedet er blevet gemt succesfuldt",
      });

      if (onUploadComplete) {
        onUploadComplete();
      }

    } catch (error) {
      console.error('Upload fejl:', error);
      toast({
        title: "Upload fejlede",
        description: "Der skete en fejl under upload af billedet. Prøv igen.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  return (
    <div className="flex items-center gap-4">
      <Input
        type="file"
        accept="image/*"
        onChange={handleFileUpload}
        disabled={uploading}
        className="file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-primary/90"
      />
      {uploading && (
        <Loader2 className="h-4 w-4 animate-spin" />
      )}
    </div>
  );
};
